import { css } from 'lit';
import { CSSResultArray, LitElement, TemplateResult, html } from 'lit';
import { customElement, query, state } from 'lit/decorators.js';
import { connect } from 'pwa-helpers/connect-mixin';
import bind from 'bind-decorator';
import { classMap } from 'lit/directives/class-map.js';
import { Ref, createRef, ref } from 'lit/directives/ref.js';
import { isValidString } from '@/utils/utils';
import { navigate } from '../../redux/redux-routing';
import store, { RootState } from '../../redux/redux-store';
import ConfigLogin, { ConfigUserManagement } from '../../config/ConfigLogin';
import resetScss from '../css/lib/reset.scss';
import bbuttonbaseScss from '../css/lib/bbuttonbase.scss';
import sharedScss from '../css/shared.scss';
import scss from '../../utils/scssAsCss';
import {
	LoginResult,
	getUser,
	selectLandingPageForUser,
	stopRefreshingAuthToken,
} from '../../redux/redux-user';
import '../common/notification-banner';
import containsTargetPenetrate from '../../utils/contains-penetrate';
import './forgot-password-dialog';
import '../user-management/password-change-dialog';
import { ModalSection } from '../../constants';
import { submitPasswordReset } from '../../redux/user-management/user-management-actions';

/**
 * @param {HTMLInputElement} inputElement resets the value and validation state of an input element
 */
function clearField(inputElement?: HTMLInputElement): void {
	if (inputElement === undefined) {
		return;
	}
	inputElement.classList.remove('invalid', 'validated');
	inputElement.value = '';
	if (inputElement.required === true) {
		//  small hack to re-initialize the validation error message state
		inputElement.required = false;
		inputElement.required = true;
	}
}

/**
 * @param {HTMLElement} element removes focus from an html element
 */
function blurElement(element?: HTMLElement): void {
	element?.blur();
}

@customElement('carsx-login')
export default class Login extends connect(store)(LitElement) {
	@state() protected invalidLogin = false;

	@state() protected isWaitingForLoginResponse = false;

	@state() protected modalView?: ModalSection | null = undefined;

	@query('#login-form') protected formElement?: HTMLFormElement;

	@query('#login-username') private usernameInput?: HTMLInputElement;

	@query('#login-password') private passwordInput?: HTMLInputElement;

	private detailsElementRef: Ref<Element> = createRef();

	private get detailsElement(): HTMLDetailsElement {
		return this.detailsElementRef.value as unknown as HTMLDetailsElement;
	}

	public static override get styles(): CSSResultArray {
		return [
			resetScss,
			bbuttonbaseScss,
			sharedScss,
			css`
@charset "UTF-8";
#login-container {
  display: flex;
  justify-content: center;
  align-items: center;
  position: fixed;
  width: 100%;
  height: 100%;
  color: #bfbfbf;
}

#login-form {
  width: 450px;
}
#login-form legend {
  text-align: center;
  width: 100%;
  padding-bottom: var(--ggutter);
}
#logo-link > img {
  max-height: 100px;
  height: 100px;
  object-fit: fill;
}

#login-form .formElements > li {
  position: relative;
  margin-bottom: var(--ggutter);
}
#login-form input,
#login-form button {
  font-size: inherit;
}
#login-form input,
#login-form button:not(#forgot-password) {
  width: 100%;
  border-radius: 3px;
}
#login-form input[type=text],
#login-form input[type=password] {
  box-sizing: border-box;
  padding: var(--ggutter);
  border: 2px solid rgb(var(--brand-primary-darkest));
  background-color: rgb(var(--brand-primary-dark));
  box-shadow: inset 0px -3px 0px 0px rgb(var(--brand-secondary));
  color: white;
  caret-color: rgb(var(--brand-secondary));
}
#login-form input[type=text] + label,
#login-form input[type=password] + label {
  position: absolute;
  top: 0px;
  left: 0px;
  padding: var(--ggutter);
  pointer-events: none;
  transition: padding-top 0.25s, font-size 0.25s;
}
#login-form input[type=text]:focus + label, #login-form input[type=text]:valid + label,
#login-form input[type=password]:focus + label,
#login-form input[type=password]:valid + label {
  font-size: 1.3rem;
  padding-top: 0px;
  margin-top: -2px;
}
#login-form input[type=text].validated:invalid, #login-form input[type=text].invalid,
#login-form input[type=password].validated:invalid,
#login-form input[type=password].invalid {
  padding-bottom: calc(var(--ggutter) / 2);
  box-shadow: none;
  background-color: #2a0816;
  border-color: #1c050e;
  caret-color: rgb(var(--brand-alert));
}
#login-form input[type=text].validated:invalid + label, #login-form input[type=text].invalid + label,
#login-form input[type=password].validated:invalid + label,
#login-form input[type=password].invalid + label {
  color: rgb(var(--brand-alert));
}
#login-form input[type=text].validated:invalid:invalid ~ .invalid-empty, #login-form input[type=text].invalid:invalid ~ .invalid-empty,
#login-form input[type=password].validated:invalid:invalid ~ .invalid-empty,
#login-form input[type=password].invalid:invalid ~ .invalid-empty {
  display: block;
}
#login-form input[type=text].validated:invalid.invalid ~ .invalid-login, #login-form input[type=text].invalid.invalid ~ .invalid-login,
#login-form input[type=password].validated:invalid.invalid ~ .invalid-login,
#login-form input[type=password].invalid.invalid ~ .invalid-login {
  display: block;
}
#login-form .invalid-note {
  display: none;
  padding: var(--ggutter) var(--ggutter) calc(var(--ggutter) / 2) calc(var(--ggutter) * 1.5);
  background-color: rgb(var(--brand-alert));
  color: white;
}
#login-form.invalid-login .invalid-login {
  display: block;
}
#login-form .form-tooltip-container > summary {
  list-style: none;
}
#login-form .form-tooltip-container > summary::before #login-form .form-tooltip-container > summary::-webkit-details-marker {
  display: none !important;
}
#login-form .form-tooltip-container .form-tooltip-content {
  position: absolute;
  z-index: 2;
  left: -50%;
  width: 140%;
  margin-top: var(--ggutter);
  padding: calc(var(--ggutter) * 2);
  background-color: white;
  border-radius: 3px;
  box-shadow: 0px 3px 6px 0px rgba(0, 0, 0, 0.16);
  color: #707070;
}
#login-form .form-tooltip-container .form-tooltip-content strong {
  font-weight: bold;
  line-height: 2em;
}
#login-form .form-tooltip-container .form-tooltip-content::before {
  content: "◢◣";
  position: absolute;
  top: calc(var(--ggutter) * -1);
  left: 50%;
  letter-spacing: -3px;
  color: white;
}
@media (max-width: 849px) {
  #login-form .form-tooltip-container .form-tooltip-content {
    box-sizing: border-box;
    left: 0px;
    bottom: calc(var(--ggutter) + 2.2rem);
    width: 100%;
    padding: var(--ggutter);
  }
  #login-form .form-tooltip-container .form-tooltip-content::before {
    content: "◥◤";
    position: absolute;
    top: unset;
    bottom: calc(var(--ggutter) * -1);
    left: 25%;
    letter-spacing: -3px;
    color: white;
  }
}
@media (max-width: 849px) {
  #login-form .form-tooltip-container .detailsOverlay {
    position: fixed;
    z-index: 999;
    top: 0px;
    left: 0px;
    width: 100%;
    height: 100%;
    cursor: auto;
    background: rgba(255, 0, 0, 0);
  }
}
#login-form button[type=submit] {
  margin-top: var(--ggutter);
  box-shadow: 0px 0px 0px 1px rgba(0, 0, 0, 0.41);
  background-color: rgb(var(--brand-secondary));
  color: black;
  font-weight: bold;
  padding: var(--ggutter);
}
#login-form button.disabled {
  opacity: 0.5;
  pointer-events: none;
}
`,
		];
	}

	protected loginFormTemplate(): TemplateResult {
		return html`<ol class="formElements">
			<div class="invalid-note invalid-login">Sorry, this Username and Password is incorrect.</div>
			<li>
				<input
					type="text"
					title="Username"
					id="login-username"
					class=${classMap({
						invalid: this.invalidLogin,
					})}
					name="login-username"
					required
					autofocus
					autocomplete="username"
					autocapitalize="none"
				/><label for="login-username">Username</label>
				<div class="invalid-note invalid-empty">You must enter a Username.</div>
			</li>
			<li>
				<input
					type="password"
					title="Password"
					id="login-password"
					class=${classMap({
						invalid: this.invalidLogin,
					})}
					name="login-password"
					required
					autocomplete="current-password"
				/><label for="login-password">Password</label>
				<div class="invalid-note invalid-empty">You must enter a Password.</div>
			</li>
			<li>
				${ConfigUserManagement.showManageUsersPage === true
					? html` <forgot-password-dialog></forgot-password-dialog> `
					: html`
							<details
								class="form-tooltip-container"
								id="forgot-password"
								${ref(this.detailsElementRef)}
							>
								<summary tabindex="0">Forgot Password?</summary>
								<div class="form-tooltip-content details-content">
									<p>
										<strong>Forgot your password?</strong><br />If you are having difficulty with
										your password, please contact your system administrator to reset your password.
									</p>
								</div>
							</details>
					  `}
			</li>
			<li>
				<button
					type="submit"
					name="login-submit"
					class="${classMap({
						bbuttonbase: true,
						cta: true,
						disabled: this.isWaitingForLoginResponse,
					})}"
				>
					Sign In
				</button>
			</li>
		</ol>`;
	}

	public override render(): TemplateResult {
		return html`
			<notification-banner></notification-banner>
			<main id="login-container">
				<form
					id="login-form"
					role="main"
					class=${classMap({
						'invalid-login': this.invalidLogin,
					})}
					@submit="${this.onSubmitForm}"
					@invalid="${{ handleEvent: this.onInvalidateForm, capture: true }}"
					@input="${{ handleEvent: this.onInputForm, capture: true }}"
				>
					<fieldset>
						<legend>
							<h1>
								<a href="${ConfigLogin.Logo.href}" id="logo-link"
									><img src="${ConfigLogin.Logo.icon}" alt="${ConfigLogin.Logo.alt}"
								/></a>
							</h1>
						</legend>
						${this.loginFormTemplate()}
					</fieldset>
				</form>
				${this.modalView === ModalSection.PASSWORD_RESET
					? html`
							<password-change-dialog
								.expectUser=${false}
								.allowBackdropClose=${false}
								@submit=${this.onSubmitPasswordReset}
								@close=${this.onClosePasswordReset}
							></password-change-dialog>
					  `
					: undefined}
			</main>
		`;
	}

	protected onInvalidateForm(e: Event): void {
		e.preventDefault();
		const target = e.target as HTMLInputElement;
		if (target !== undefined) {
			target.classList.add('validated');
			const firstInvalidInput: HTMLInputElement | undefined | null =
				target?.form?.querySelector('input:invalid');
			if (firstInvalidInput !== undefined && firstInvalidInput !== null) {
				firstInvalidInput.focus();
				firstInvalidInput.select();
			}
		}
	}

	override stateChanged(state: RootState): void {
		this.modalView = state.routing.modalView;
	}

	protected handleLoginAttempt(loginWassuccessful: boolean): void {
		if (loginWassuccessful === true) {
			this.invalidLogin = false;
			Array.from([this.usernameInput, this.passwordInput]).forEach(clearField);
			this.formElement?.querySelectorAll<HTMLElement>(':focus').forEach(blurElement);
			this.formElement?.reset();

			const landingPage = selectLandingPageForUser();

			void store.dispatch(navigate(landingPage));
		} else {
			this.invalidLogin = true;
			this.usernameInput?.focus();
			this.usernameInput?.select();
		}
	}

	@bind
	protected async onSubmitForm(e: Event): Promise<void> {
		e.preventDefault();
		this.isWaitingForLoginResponse = true;

		const username = this.usernameInput?.value;
		const password = this.passwordInput?.value;

		if (!isValidString(username) || !isValidString(password)) {
			this.isWaitingForLoginResponse = false;
			return;
		}

		const loginResult = await store.dispatch(getUser(username, password));
		if (loginResult === LoginResult.Success) {
			this.handleLoginAttempt(true);
		}
		if (loginResult === LoginResult.InvalidCredentials) {
			this.handleLoginAttempt(false); //	login request completed, but access token was not provided by server
		}
		this.isWaitingForLoginResponse = false;
	}

	@bind
	private onInputForm(): void {
		this.invalidLogin = false; //	reset invalid login state if they try to enter new information
	}

	@bind private onGlobalMouseup(e: MouseEvent): void {
		if (this.detailsElement?.open === true && !containsTargetPenetrate(this.detailsElement, e)) {
			this.detailsElement.open = false;
			this.detailsElement.blur();
		}
	}

	onClosePasswordReset() {
		void store.dispatch(navigate('#')); // clear modalView
	}

	async onSubmitPasswordReset(event: CustomEvent<{ password: string; closeFn: () => void }>) {
		try {
			await store.dispatch(submitPasswordReset(event.detail.password));
		} catch (e) {
			console.error(e);
		} finally {
			event.detail.closeFn();
			void store.dispatch(navigate('#'));
		}
	}

	public override connectedCallback(): void {
		window.addEventListener('mouseup', this.onGlobalMouseup);
		super.connectedCallback();

		stopRefreshingAuthToken();
	}

	public override disconnectedCallback(): void {
		window.removeEventListener('mouseup', this.onGlobalMouseup);
		super.disconnectedCallback();
	}
}
